<template>
	<client-only>
		<button
			:class="[boxClass, 'cursor-pointer hover:bg-gray  hover:bg-opacity-50']"
			name="Add to wishlist"
			:aria-label="$t('add to wishlist')"
			@click="handleClick"
		>
			<HeartIconSolid
				v-if="isInWishlist"
				class="h-6 w-6 text-primary favorite__icon"
			/>
			<HeartIconOutline v-else class="h-6 w-6 text-primary favorite__icon" />
		</button>
		<!-- Only the wishlist state needs the client; the box has to exist on the
		server too, or hydration inserts it and reflows the row around it. -->
		<template #fallback>
			<div :class="boxClass" aria-hidden="true"></div>
		</template>
	</client-only>
</template>

<script setup lang="ts">
import { HeartIcon as HeartIconSolid } from "@heroicons/vue/24/solid";
import { HeartIcon as HeartIconOutline } from "@heroicons/vue/24/outline";
import { storeToRefs } from "pinia";
import { useGtmEvents } from "~/layers/base/composables/useGtmEvents";
import type {
	Product,
	ProductItem
} from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import type { FilteredProductItem } from "@nwt/norce-pwa/dist/runtime/types/api/searchAndFilter/filters";

interface Props {
	wrapperSize: string | number;
	rounded: string;
	absolute?: boolean;
	border?: { color: string; weight: number };
	product: Product | Partial<FilteredProductItem> | ProductItem;
	variantParametricsNumber?: number;
}

const { $toast } = useNuxtApp();

const {
	wrapperSize,
	rounded,
	absolute,
	border,
	variantParametricsNumber,
	product
} = defineProps<Props>();
const { updateWishList } = useWishlistStore();
const { wishlistItems } = storeToRefs(useWishlistStore());
const { addToWishlist: addToWishlistGtm } = useGtmEvents();

const { selectedColor, selectedSecondParametric } =
	storeToRefs(useProductStore());

const { t } = useI18n();
const { $i18n } = useNuxtApp();

const productIdValue = computed(() => product.Id);
const isInWishlist = computed(
	() =>
		productIdValue.value !== undefined &&
		wishlistItems.value.includes(productIdValue.value)
);

const handleClick = (e: MouseEvent) => {
	e.preventDefault();
	if (productIdValue.value === undefined) return;
	addToWishlist(productIdValue.value);
};

// Shared by the button and its SSR placeholder so both occupy the same box.
// `favorite` is a styling hook (styled in sportkost and aworldofcraft).
const boxClass = computed(() => [
	`flex items-center z-10  bg-white bg-opacity-50 rounded-${rounded} w-${wrapperSize} h-${wrapperSize} justify-center right-6 top-2 favorite`,
	absolute && "absolute",
	border && `border-${border.weight}  border-${border.color}`
]);

const addToWishlist = (productId: number) => {
	if (!variantParametricsNumber) {
		if (!wishlistItems.value?.includes(productId)) {
			addToWishlistGtm(product);
		}

		updateWishList(productId);

		return $toast.success($i18n.t("Wishlist updated"));
	}

	const allProductParametricsSelected =
		variantParametricsNumber === 1
			? selectedColor.value || selectedSecondParametric.value
			: selectedColor.value && selectedSecondParametric.value;

	if (allProductParametricsSelected) {
		if (!wishlistItems.value?.includes(productId)) {
			addToWishlistGtm(product);
		}

		updateWishList(productId);

		return $toast.success($i18n.t("Wishlist updated"));
	} else {
		return $toast.error(
			t("You need to select {count} product attributes", {
				count: variantParametricsNumber
			})
		);
	}
};
</script>
